// What a student types in the main chat (outside a test), read on this device (F13).
//
// The main chat used to send every typed line to the old Dialogflow agent, which had no answer
// for everyday questions and locked the chat after five misses. Now a short keyword list in
// English, Filipino and Bisaya decides where to take the student, and anything it doesn't know
// gets a kind reply with big buttons. Nothing leaves the phone, and the chat never locks.
//
// Order matters only for ties: the crisis check (crisisPhrases.js) always runs before this, in
// Chatbot.send(), and its result is never overridden.

/** lowercase, no accents, punctuation as spaces, runs of 3+ of the same letter cut to one. */
export const normalizeText = (text) =>
  String(text ?? '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .replace(/['’‘`´]/g, '')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/(\p{L})\1{2,}/gu, '$1')
    .split(/\s+/)
    .filter(Boolean)
    .join(' ');

// The tests by the words students use for them
const TEST_WORDS = {
  riasec: ['riasec', 'career', 'careers', 'career interest*', 'course test', 'interest test', 'kurso', 'karera', 'trabaho'],
  dass42: ['dass*', 'mood', 'stress', 'mood and stress', 'anxiety', 'depression'],
  ryff: ['ryff', 'well being', 'wellbeing', 'well-being', 'kaayohan', 'kagalingan'],
  sdq: ['sdq', 'strengths', 'strength', 'difficulties', 'strengths and difficulties'],
};

// Keywords per intent. '*' at the end of a word stands for any ending ("result*" covers results,
// resulta). Phrases match whole words in order.
const INTENTS = [
  {
    key: 'continue_test',
    words: [
      'continue', 'resume', 'pick up where', 'where i left', 'left off', 'unfinished', 'not finished', 'finish my test',
      'ituloy', 'ipagpatuloy', 'tuloy', 'balikan', 'tapusin', 'hindi ko natapos', 'di ko natapos',
      'padayon', 'ipadayon', 'tapuson', 'wala nako nahuman', 'wala pa nahuman',
    ],
  },
  {
    key: 'deadline',
    words: [
      'deadline*', 'due', 'due date', 'until when', 'last day', 'when is the test', 'when do i',
      'hanggang kailan', 'kailan ang', 'kailan ba', 'huling araw', 'taning',
      'kanus a', 'kanusa', 'hangtod kanus a', 'hangtod kanusa', 'ulahing adlaw',
    ],
  },
  {
    key: 'results',
    words: [
      'result*', 'score*', 'my answers', 'how did i do', 'outcome',
      'resulta*', 'marka', 'iskor', 'kinalabasan', 'nakuha ko',
      'resulta nako', 'akong resulta', 'akong result*',
    ],
  },
  {
    key: 'my_session',
    words: [
      'my session*', 'my appointment*', 'next session', 'next appointment', 'when is my session',
      'schedule ko', 'aking session', 'session ko', 'appointment ko', 'akong session', 'akong appointment',
    ],
  },
  {
    key: 'book',
    words: [
      'book*', 'appointment*', 'session*', 'talk to', 'talk with', 'speak to', 'speak with', 'meet', 'counsel*',
      'guidance', 'consult*', 'someone to talk', 'need help', 'help me', 'need someone',
      'kausap', 'kausapin', 'makausap', 'makipag usap', 'magpa schedule', 'magpaschedule', 'tulong', 'tulungan',
      'istorya', 'storya', 'makig istorya', 'makigstorya', 'makaistorya', 'tabang', 'tabangi', 'kinahanglan ko ug',
    ],
  },
  {
    key: 'about_tests',
    words: [
      'what is', 'whats', 'what are', 'what does', 'what test*', 'each test', 'about the test*', 'explain', 'meaning',
      'para saan', 'ano ang', 'ano yung', 'ano ba ang', 'ano yan', 'anong test', 'ibig sabihin',
      'unsa ang', 'unsa na', 'unsay', 'para asa', 'unsa man', 'kahulugan',
    ],
  },
  {
    key: 'take_test',
    // No bare "take": "I can't take it anymore" is not a request for a test
    words: [
      'start', 'begin', 'answer the test', 'test*', 'exam*', 'assessment*', 'survey', 'questionnaire',
      'sagutan', 'sasagutan', 'magsagot', 'mag test', 'magtest', 'simulan', 'umpisahan',
      'tubagon', 'motubag', 'magsugod', 'sugdan', 'mag take',
    ],
  },
  {
    key: 'thanks',
    words: ['thank*', 'thanks', 'ty', 'tysm', 'salamat*', 'maraming salamat', 'daghang salamat'],
  },
  {
    key: 'greeting',
    words: [
      'hi', 'hello', 'hey', 'helo', 'hii', 'yo', 'good morning', 'good afternoon', 'good evening',
      'kumusta', 'kamusta', 'musta', 'magandang umaga', 'magandang hapon', 'magandang gabi',
      'maayong buntag', 'maayong hapon', 'maayong gabii', 'maayong udto',
    ],
  },
];

const escape = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Whole words in order; a trailing '*' lets the last word go on ("result*" covers resulta)
const toPattern = (phrase) => {
  const open = phrase.trim().endsWith('*');
  const words = normalizeText(phrase.replace(/\*/g, '')).split(' ').filter(Boolean).map(escape);
  return `(?:^|\\s)${words.join('\\s+')}${open ? '[\\p{L}\\p{N}]*' : ''}(?=\\s|$)`;
};

const compileList = (words) => words.map((word) => new RegExp(toPattern(word), 'u'));

const COMPILED = INTENTS.map((intent) => ({ key: intent.key, patterns: compileList(intent.words) }));
const TEST_COMPILED = Object.entries(TEST_WORDS).map(([type, words]) => ({ type, patterns: compileList(words) }));

/** The test a line names ("what is the ryff", "start the career test"), or null. */
export const testNamed = (text) => {
  const clean = normalizeText(text);
  const found = TEST_COMPILED.find(({ patterns }) => patterns.some((pattern) => pattern.test(clean)));
  return found ? found.type : null;
};

/**
 * { intent, test } for one typed line: intent is one of continue_test, deadline, results,
 * my_session, book, about_tests, take_test, thanks, greeting, or null when nothing fits.
 * The intent with the most keyword hits wins; ties go to the one listed first.
 */
export const matchIntent = (text) => {
  const clean = normalizeText(text);
  const test = clean ? testNamed(clean) : null;
  if (!clean) return { intent: null, test: null };
  let best = null;
  let bestHits = 0;
  COMPILED.forEach(({ key, patterns }) => {
    const hits = patterns.filter((pattern) => pattern.test(clean)).length;
    if (hits > bestHits) {
      best = key;
      bestHits = hits;
    }
  });
  // "hi, can I see my results" is about results; a greeting only wins on its own
  if (best === 'greeting' || best === 'thanks') {
    const other = COMPILED.filter(({ key }) => key !== 'greeting' && key !== 'thanks')
      .find(({ patterns }) => patterns.some((pattern) => pattern.test(clean)));
    if (other) best = other.key;
  }
  // A test's name on its own ("ryff?", "career test") means "tell me about it"
  if (!best && test) best = 'about_tests';
  return { intent: best, test };
};

// ---------- what Anna says back --------------------------------------------------------------

const tx = (lang, en, fil) => (lang === 'fil' ? fil : en);

export const TEST_NAMES = {
  riasec: { en: 'Career interests', fil: 'Career interests' },
  dass42: { en: 'Mood and stress', fil: 'Mood and stress' },
  ryff: { en: 'Well-being', fil: 'Well-being' },
  sdq: { en: 'Strengths and difficulties', fil: 'Strengths and difficulties' },
};
const nameOf = (type, lang) => (TEST_NAMES[type] ? TEST_NAMES[type][lang === 'fil' ? 'fil' : 'en'] : type);

// What each test is about, in words a 16-year-old gets on their own (no scores, no jargon)
const ABOUT = {
  riasec: {
    en: 'Career interests asks what kinds of activities you like, to help you think about college programs and work that could fit you.',
    fil: 'Ang Career interests ay nagtatanong kung anong mga gawain ang gusto mo, para matulungan kang mag-isip ng kurso at trabahong bagay sa iyo.',
  },
  dass42: {
    en: "Mood and stress asks how you've been feeling lately, like feeling down, worried or stressed.",
    fil: 'Ang Mood and stress ay nagtatanong kung ano ang nararamdaman mo nitong mga nakaraang araw, gaya ng lungkot, pag-aalala o stress.',
  },
  ryff: {
    en: "Well-being asks how you're doing in life overall, like your goals, your relationships and how you see yourself.",
    fil: 'Ang Well-being ay nagtatanong kung kumusta ka sa kabuuan, gaya ng mga layunin mo, mga relasyon mo at tingin mo sa sarili mo.',
  },
  sdq: {
    en: "Strengths and difficulties asks about what you're good at and what has been hard for you, with friends, feelings and behavior.",
    fil: 'Ang Strengths and difficulties ay nagtatanong kung saan ka magaling at kung ano ang mahirap para sa iyo, sa kaibigan, damdamin at kilos.',
  },
};

/** The buttons Anna can offer. `go` is what Chatbot does with a tap. */
export const ACTIONS = {
  choose: { go: 'choose', icon: 'play', en: 'Take a test', fil: 'Kumuha ng test' },
  continue: { go: 'choose', icon: 'play', en: 'Continue my test', fil: 'Ituloy ang test ko' },
  results: { go: 'results', icon: 'bar-chart', en: 'My results', fil: 'Mga resulta ko' },
  openResults: { go: 'results', icon: 'bar-chart', en: 'Open my results', fil: 'Buksan ang resulta ko' },
  book: { go: 'book', icon: 'calendar-plus', en: 'Talk to the guidance office', fil: 'Kausapin ang guidance office' },
  sessions: { go: 'sessions', icon: 'calendar', en: 'See my sessions', fil: 'Tingnan ang mga session ko' },
  about: { go: 'about', icon: 'help-circle', en: 'What is each test?', fil: 'Ano ang bawat test?' },
};

/** The four big buttons shown when Anna doesn't understand. */
export const HELP_ACTIONS = ['choose', 'results', 'book', 'about'];

const action = (key, lang, extra = {}) => ({
  key,
  go: ACTIONS[key].go,
  icon: ACTIONS[key].icon,
  label: tx(lang, ACTIONS[key].en, ACTIONS[key].fil),
  ...extra,
});

const formatDue = (due, lang) => {
  if (!due) return '';
  const [y, m, d] = String(due).split('-').map(Number);
  const date = new Date(y, (m || 1) - 1, d || 1);
  return date.toLocaleDateString(lang === 'fil' ? 'fil-PH' : 'en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

/**
 * { text, actions } for an intent. `tests` is the /api/my-tests/ list when it is known (for due
 * dates); `name` is the student's first name.
 */
export const replyFor = ({ intent, test = null }, { lang = 'en', name = '', tests = null } = {}) => {
  switch (intent) {
    case 'continue_test':
      return {
        text: tx(lang, "Let's pick up where you left off. Your answers so far are saved.",
          'Ituloy natin kung saan ka tumigil. Naka-save ang mga sagot mo.'),
        actions: [action('continue', lang, test ? { test } : {})],
      };
    case 'deadline': {
      const due = (tests || [])
        .filter((row) => row.due && (row.status === 'open' || row.status === 'in_progress'))
        .filter((row) => !test || row.type === test)
        .sort((a, b) => String(a.due).localeCompare(String(b.due)));
      if (!due.length) {
        return {
          text: tx(lang, "None of your tests has a due date right now. Open the test list to see what's ready for you.",
            'Wala pang due date ang mga test mo ngayon. Buksan ang listahan ng test para makita ang handa para sa iyo.'),
          actions: [action('choose', lang)],
        };
      }
      const lines = due.map((row) => `${nameOf(row.type, lang)}: ${tx(lang, 'due', 'hanggang')} ${formatDue(row.due, lang)}`);
      return {
        text: `${tx(lang, 'Here are your due dates:', 'Ito ang mga due date mo:')} ${lines.join(' · ')}`,
        actions: [action('choose', lang)],
      };
    }
    case 'results':
      return {
        text: tx(lang, "Here's where your results are.", 'Narito ang mga resulta mo.'),
        actions: [action('openResults', lang, test ? { test } : {})],
      };
    case 'my_session':
      return {
        text: tx(lang, 'Your sessions with the guidance office are on the Appointments page.',
          'Nasa Appointments page ang mga session mo sa guidance office.'),
        actions: [action('sessions', lang), action('book', lang)],
      };
    case 'book':
      return {
        text: tx(lang, 'You can book a time to talk with someone from the guidance office.',
          'Puwede kang mag-book ng oras para makausap ang guidance office.'),
        actions: [action('book', lang)],
      };
    case 'about_tests': {
      const types = test ? [test] : ['riasec', 'dass42', 'ryff', 'sdq'];
      return {
        text: types.map((type) => ABOUT[type][lang === 'fil' ? 'fil' : 'en']).join(' '),
        actions: [action('choose', lang)],
      };
    }
    case 'take_test':
      return {
        text: tx(lang, "Pick a test when you're ready. I'll take you through it one question at a time.",
          'Pumili ng test kapag handa ka na. Isa-isa nating sasagutin ang mga tanong.'),
        actions: [action('choose', lang, test ? { test } : {})],
      };
    case 'thanks':
      return {
        text: tx(lang, "You're welcome! I'm here if you need anything else.", 'Walang anuman! Nandito lang ako kung may kailangan ka pa.'),
        actions: [],
      };
    case 'greeting':
      return {
        text: name
          ? tx(lang, `Hi, ${name}! What would you like to do?`, `Kumusta, ${name}! Ano ang gusto mong gawin?`)
          : tx(lang, 'Hi! What would you like to do?', 'Kumusta! Ano ang gusto mong gawin?'),
        actions: HELP_ACTIONS.map((key) => action(key, lang)),
      };
    default:
      return {
        text: tx(lang, "I'm not sure I got that. Here's what I can help with:", 'Hindi ko yata naintindihan. Ito ang mga puwede kong itulong:'),
        actions: HELP_ACTIONS.map((key) => action(key, lang)),
      };
  }
};

/** What Anna says when a typed line has worrying words (crisisPhrases.js); the crisis lines show between. */
export const crisisReply = (lang = 'en') => ({
  text: tx(lang, "Thank you for telling me. You don't have to go through this alone.",
    'Salamat sa pagsabi mo sa akin. Hindi mo kailangang harapin ito nang mag-isa.'),
  after: tx(lang, 'You can also talk to someone from the guidance office. I can help you book a time.',
    'Puwede mo ring kausapin ang guidance office. Matutulungan kitang mag-book ng oras.'),
  actions: [action('book', lang, { reason: 'personal' })],
});
